import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ReactLenis, useLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowLeft, Play, Crown, Trophy } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { ShowreelProvider, useShowreel } from "@/components/site/Showreel";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { resolveTheme } from "@/lib/festivalTheme";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { FestivalFilmModal } from "@/components/site/FestivalFilmModal";
import { FestivalJuryModal } from "@/components/site/FestivalJuryModal";
import { resolveJuryMembers } from "@/lib/festivalJury";
import { JoinDialog } from "@/components/site/JoinDialog";
import { AwardLabel, filmAward, laurelSrc, prizeCardStyle } from "@/components/site/FestivalGraphics";
import { FestivalEditionsStrip, FestivalJuryRow, POSTER_BLOCK, posterImgStyle, HScroll } from "@/components/site/FestivalShowcase";
import { ArchiveHeader } from "@/components/site/ArchiveHeader";
import { resolveUrl, galleryCover } from "@/lib/media";

const visibleFilms = (ed) =>
  (ed.films || [])
    .filter((f) => f && !f.hidden && (f.title || f.poster))
    .map((f, i) => ({ ...f, _i: i }))
    .sort((a, b) => {
      // Grand winner (principal award) always first so it reads immediately.
      const pa = filmAward(a)?.principal ? 1 : 0;
      const pb = filmAward(b)?.principal ? 1 : 0;
      if (pa !== pb) return pb - pa;
      return (a.order ?? a._i) - (b.order ?? b._i);
    });

// (Header d'édition partagé : voir components/site/ArchiveHeader.jsx)

const EditionBody = () => {
  const { content } = useContent();
  const { slug } = useParams();
  const showreel = useShowreel();
  const lenis = useLenis();
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("all");
  const [joinOpen, setJoinOpen] = useState(false);
  const [juryModalOpen, setJuryModalOpen] = useState(false);
  const goFilms = () => {
    const el = document.getElementById("edition-films");
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -90 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fa = content.festivalArchive || {};
  const ed = (fa.editions || []).find((e) => !e.hidden && (String(e.id) === slug || String(e.year) === slug));

  const films = ed ? visibleFilms(ed) : [];
  const winner = films.find((f) => filmAward(f)?.principal) || null;
  const winnerAward = winner ? filmAward(winner) : null;
  const jury = ed ? resolveJuryMembers(ed, fa.juryLibrary).filter((m) => m && !m.hidden && (m.name || m.photo)) : [];
  const juryColorMode = ed?.juryColorMode || fa.juryColorMode || "hover";
  // Distinct prize names used in this edition (for the filter bar); prizes stay optional.
  const usedAwards = [...new Set(films.map((f) => filmAward(f)?.name).filter(Boolean))];
  const hasUnawarded = films.some((f) => !filmAward(f));
  const shown = films.filter((f) => {
    if (filter === "all") return true;
    if (filter === "none") return !filmAward(f);
    return filmAward(f)?.name === filter;
  });

  const Chip = ({ id, label }) => (
    <button onClick={() => setFilter(id)} data-testid={`festival-filter-${id}`}
      className={`px-4 py-2 rounded-full text-[11px] tracking-[0.12em] uppercase border transition-colors ${filter === id ? "bg-electric text-black border-electric" : "text-white/60 border-white/15 hover:border-electric hover:text-electric"}`}>
      {label}
    </button>
  );

  return (
    <div className="App festival-universe min-h-screen flex flex-col" style={{ ...resolveTheme(fa.theme, ed), backgroundColor: "var(--fx-bg)", color: "var(--fx-text)" }}>
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <ArchiveHeader brand={content.brand || {}} edition={ed} onApply={() => setJoinOpen(true)} onFilms={goFilms} active="films" />
      <main className="flex-1 pt-24 md:pt-32 pb-24">
        {!ed ? (
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
            <div className="text-[11px] tracking-[0.35em] uppercase text-electric mb-4">Erreur 404</div>
            <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">Édition introuvable</h1>
            <Link to="/festival" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white rounded-full text-[13px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors"><ArrowLeft size={15} /> Retour au Festival</Link>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-6 md:px-12" data-testid="festival-edition-page">
            <Reveal>
              <Link to="/festival" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-8"><ArrowLeft size={14} /> Festival</Link>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[11px] tracking-[0.3em] uppercase text-electric">Karams Festival</span>
                <span data-testid="edition-status-badge" className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border ${ed.current ? "border-electric/50 text-electric bg-electric/10" : "border-white/20 text-white/60"}`}>
                  {ed.current ? "Édition actuelle" : "Archive"}
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(300px,40%)] gap-8 lg:gap-12 items-center mb-12">
                <div>
                  <h1 className="fx-display font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-6">Édition <span className="text-electric">{ed.year}</span></h1>
                  {ed.description && <p className="text-lg md:text-xl text-white/85 font-light leading-relaxed">{ed.description}</p>}
                  {ed.reelUrl && ed.teaserEnabled !== false && (
                    <button onClick={() => showreel.open(ed.reelUrl, `Karams Festival ${ed.year}`, "Teaser")}
                      data-testid="festival-edition-teaser-btn"
                      className="group mt-6 inline-flex items-center gap-3 pl-2 pr-6 py-2 rounded-full border border-electric/50 text-electric text-[12px] tracking-[0.14em] uppercase font-semibold hover:bg-electric hover:text-black transition-colors">
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-electric/20 group-hover:bg-black/20 transition-colors"><Play size={14} className="ml-0.5" /></span>
                      Découvrir le teaser
                    </button>
                  )}
                </div>
                {winner && (() => {
                  const c = winnerAward.color;
                  const awText = c === "bronze" ? "#cd8f52" : c === "white" ? "#dfe3ea" : "#e7c56a";
                  const wCover = winner.coverImage;
                  const wPoster = winner.poster || galleryCover(winner.media);
                  return (
                    <Link to={`/festival/palmares?year=${ed.year}`} className="group relative block rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] p-5" data-testid="edition-winner-highlight">
                      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(110% 80% at 100% 0%, ${awText}22, transparent 60%)` }} />
                      <img src={laurelSrc(c)} onError={(ev) => { ev.currentTarget.src = laurelSrc("gold"); }} alt="" className="absolute -right-10 -bottom-10 h-44 opacity-[0.08] object-contain pointer-events-none" />
                      <div className="relative">
                        <div className="text-[10px] tracking-[0.3em] uppercase mb-4 font-semibold" style={{ color: awText }}>Grand Prix du Jury</div>
                        <div className="flex gap-4 items-center">
                          <div className={`${wCover ? "aspect-[16/10] w-44" : POSTER_BLOCK + " w-24"} rounded-lg overflow-hidden border border-white/15 bg-[#0e0e0e] shrink-0`}>
                            {(wCover || wPoster)
                              ? <img src={resolveUrl(wCover || wPoster)} alt={winner.title} className="w-full h-full object-cover" style={wCover ? undefined : posterImgStyle(winner)} />
                              : <div className="w-full h-full flex items-center justify-center text-white/30 text-xs px-2 text-center">{winner.title}</div>}
                          </div>
                          <div className="min-w-0">
                            <div className="font-serif text-2xl text-white leading-tight line-clamp-2">{winner.title}</div>
                            {(winner.laureate || winner.director) && <div className="text-xs text-secondary2 mt-1">{winner.laureate || winner.director}</div>}
                            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase text-electric mt-3 group-hover:gap-2.5 transition-all"><Trophy size={13} /> Voir le palmarès</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })()}
              </div>
            </Reveal>

            {/* Films grid + optional award filter */}
            {films.length > 0 && (
              <Reveal>
                <div id="edition-films" className="scroll-mt-28" />
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                  <h2 className="fx-section font-serif text-3xl md:text-4xl text-white">Les films <span className="text-secondary2 text-xl">({films.length})</span></h2>
                  {ed.showFilmFilters && (usedAwards.length > 0 || hasUnawarded) && (
                    <div className="flex flex-wrap gap-2" data-testid="festival-edition-filters">
                      <Chip id="all" label="Tous" />
                      {usedAwards.map((name) => <Chip key={name} id={name} label={name} />)}
                      {hasUnawarded && <Chip id="none" label="Non primés" />}
                    </div>
                  )}
                </div>

                <HScroll testid="festival-edition-films-scroll">
                  {shown.map((film, i) => {
                    const aw = filmAward(film);
                    const poster = film.poster || galleryCover(film.media);
                    const isWinner = aw?.principal;
                    const pStyle = aw ? prizeCardStyle(aw, fa.prizeStyles) : null;
                    return (
                      <button key={film.id || i} onClick={() => setSel(film)} data-testid={`edition-film-${i}`} className="group snap-start shrink-0 w-[158px] sm:w-[182px] text-left">
                        <div className="h-6 mb-1.5 flex items-center justify-center">
                          {aw && <AwardLabel name={aw.name} color={aw.color} size={aw.size} testid={`edition-film-award-${i}`} />}
                        </div>
                        <div style={pStyle || undefined} className={`relative aspect-[2/3] rounded-lg overflow-hidden border bg-[#0e0e0e] transition-colors ${aw ? "" : "border-white/10 group-hover:border-electric/50"}`}>
                          {poster
                            ? <img src={resolveUrl(poster)} alt={film.title} loading="lazy" className="w-full h-full object-cover" style={posterImgStyle(film)} />
                            : <div className="w-full h-full flex items-center justify-center text-white/30 text-sm px-3 text-center">{film.title}</div>}
                          {isWinner && (
                            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-400/90 text-black text-[9px] tracking-[0.15em] uppercase font-semibold" data-testid={`edition-film-winner-${i}`}><Crown size={11} /> Grand Gagnant</span>
                          )}
                        </div>
                        <div className="mt-2.5">
                          <div className="fx-film-title text-sm text-white leading-snug line-clamp-1 group-hover:text-electric transition-colors">{film.title}</div>
                          {film.duration && <div className="text-xs text-secondary2 mt-0.5">{film.duration}</div>}
                        </div>
                      </button>
                    );
                  })}
                </HScroll>
                {shown.length === 0 && <p className="text-secondary2 text-sm mt-6">Aucun film pour ce filtre.</p>}
              </Reveal>
            )}

            {/* Jury of this edition */}
            {jury.length > 0 && (
              <Reveal className="mt-16">
                <FestivalJuryRow members={jury} year={ed.year} colorMode={juryColorMode} onJury={() => setJuryModalOpen(true)} />
              </Reveal>
            )}

            {/* Switch to another edition */}
            <Reveal className="mt-20 pt-12 border-t border-white/10" testid="edition-others">
              <FestivalEditionsStrip editions={fa.editions || []} currentId={ed.id || ed.year} />
            </Reveal>
          </div>
        )}
      </main>
      <Footer />
      <FestivalFilmModal film={sel} editionYear={ed?.year} onClose={() => setSel(null)} />
      <FestivalJuryModal edition={juryModalOpen ? { ...ed, jury } : null} colorMode={juryColorMode} onClose={() => setJuryModalOpen(false)} />
      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} type="festival" refId="festival"
        refTitle={`Karams Festival — Édition ${ed?.year || ""}`} roles={content.festival?.joinRoles || []}
        notice={content.festival?.participation?.notice} documents={content.festival?.participation?.documents || []}
        heading="Soumettre ma candidature" intro="Candidatez, devenez bénévole ou partenaire du Karams Festival." />
    </div>
  );
};

export default function FestivalEditionPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <ShowreelProvider>
          <Analytics />
          <EditionBody />
        </ShowreelProvider>
      </ReactLenis>
    </ContentProvider>
  );
}
